import { useEffect, useRef } from 'react';
import { useIsKeepAliveActive } from '../context/KeepAliveActiveContext';

interface UseOSKeepAliveRefreshArgs {
  /** The normativ shown in the editor (null/undefined while creating or empty). */
  ingredientId?: string | null;
  isDirty: boolean;
  /** Archive view / create mode — nothing live to refresh. */
  disabled: boolean;
  refreshIngredientIfChanged: (id: string, canApply: () => boolean) => Promise<boolean>;
  /** Called after a newer version replaced the shown one — the screen re-syncs its form. */
  onRefreshed: () => void;
}

/**
 * /os stays mounted while hidden (keep-alive tab), so another user's change is not picked up
 * by a remount. When the tab becomes visible again and there are NO unsaved edits, re-read the
 * normativ silently and re-sync the form if its version moved on. With unsaved edits nothing is
 * overwritten — the server's expectedVersion check then answers the save with a 409.
 */
export const useOSKeepAliveRefresh = ({
  ingredientId,
  isDirty,
  disabled,
  refreshIngredientIfChanged,
  onRefreshed,
}: UseOSKeepAliveRefreshArgs) => {
  const isActive = useIsKeepAliveActive();
  const wasActiveRef = useRef(isActive);
  const latest = useRef({ ingredientId, isDirty, disabled, refreshIngredientIfChanged, onRefreshed });
  latest.current = { ingredientId, isDirty, disabled, refreshIngredientIfChanged, onRefreshed };

  useEffect(() => {
    const becameActive = isActive && !wasActiveRef.current;
    wasActiveRef.current = isActive;
    const args = latest.current;
    if (!becameActive || args.disabled || !args.ingredientId || args.isDirty) return;
    // Re-checked right before applying: the user may start typing while the GET is in flight.
    args.refreshIngredientIfChanged(args.ingredientId, () => !latest.current.isDirty)
      .then((changed) => {
        if (changed) latest.current.onRefreshed();
      });
  }, [isActive]);
};
